import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import KaringanaBotWidget from "../components/KaringanaBotWidget";
import WhatsAppSupportButton from "../components/WhatsAppSupportButton";
import KaringanaInstallPrompt from "../components/KaringanaInstallPrompt";
import SplashScreen from "../components/SplashScreen";

// Componente de Partículas de Fundo
const BackgroundParticles = () => {
  const particles = Array(20)
    .fill()
    .map((_, i) => ({
      id: i,
      size: Math.random() * 8 + 2,
      x: Math.random() * 100,
      y: Math.random() * 100,
      duration: Math.random() * 30 + 20,
      delay: Math.random() * 10,
    }));

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        zIndex: 0,
      }}
      aria-hidden="true"
    >
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          style={{
            position: "absolute",
            width: particle.size,
            height: particle.size,
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.4)",
            top: `${particle.y}%`,
            left: `${particle.x}%`,
          }}
          animate={{
            y: ["0%", "100%"],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: "linear",
          }}
        />
      ))}
    </div>
  );
};

// Componente de Crachá de Destaque
const HighlightBadge = ({ text }) => {
  return (
    <div
      style={{
        position: "absolute",
        top: "-15px",
        right: "-15px",
        backgroundColor: "#FFEB3B",
        color: "#000",
        padding: "5px 12px",
        borderRadius: "20px",
        fontSize: "0.8rem",
        fontWeight: "bold",
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        transform: "rotate(15deg)",
        zIndex: 2,
        userSelect: "none",
      }}
      aria-label={text}
    >
      {text}
    </div>
  );
};

// Componente para Cartão de Notícias
const NewsCard = ({ item, isActive }) => {
  const cardColors = {
    ranking: "#D32F2F",
    novidades: "#2E7D32",
    curiosidades: "#1565C0",
    draiva: "#FF9800",
  };

  const getCardColor = () => {
    if (item.title.includes("Draiva")) return cardColors.draiva;
    if (item.title.includes("Ranking")) return cardColors.ranking;
    if (item.title.includes("Novidades")) return cardColors.novidades;
    return cardColors.curiosidades;
  };

  return (
    <motion.div
      initial={{ opacity: 0.6, scale: 0.9 }}
      animate={{
        opacity: isActive ? 1 : 0.6,
        scale: isActive ? 1 : 0.9,
        y: isActive ? -10 : 0,
      }}
      transition={{ duration: 0.5 }}
      style={{
        padding: "25px",
        backgroundColor: getCardColor(),
        borderRadius: "15px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
        margin: "10px",
        position: "relative",
        overflow: "hidden",
      }}
      role="group"
      aria-roledescription="slide"
      aria-label={item.title}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage:
            "radial-gradient(circle at 10% 10%, rgba(255,255,255,0.15), transparent 60%)",
          zIndex: 0,
        }}
        aria-hidden="true"
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        <h3
          style={{
            color: "white",
            fontSize: "1.4rem",
            marginBottom: "10px",
            textShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          {item.title}
        </h3>
        <p
          style={{
            color: "white",
            fontSize: "1.1rem",
            lineHeight: "1.5",
            textShadow: "0 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          {item.content}
        </p>
        {item.title.includes("Draiva") && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.open("https://unitymoz-draiva.web.app", "_blank")}
            style={{
              backgroundColor: "white",
              color: cardColors.draiva,
              padding: "8px 16px",
              borderRadius: "20px",
              border: "none",
              marginTop: "15px",
              fontWeight: "bold",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span>🎯 Visitar</span>
          </motion.button>
        )}
      </div>
      

    </motion.div>
  );
};

// Componente para Slider de Notícias
const NewsSlider = () => {
  const [activeSlide, setActiveSlide] = useState(0);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 700,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: false,
    accessibility: true,
    beforeChange: (current, next) => setActiveSlide(next),
    customPaging: () => (
      <div
        style={{
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          backgroundColor: "white",
          opacity: 0.7,
          transition: "all 0.3s ease",
        }}
      />
    ),
    dotsClass: "slick-dots custom-dots",
  };

  const sliderStyle = {
    position: "relative",
    zIndex: 1,
    width: "95%",
    maxWidth: "800px",
    marginTop: "30px",
    marginBottom: "50px",
  };

  const newsItems = [
  {
    title: "🚗 Draiva",
    content:
      "Prepare-se para o exame de condução com o Draiva App! Exames teóricos do INATRO, dicas e muito mais para ajudar você a conquistar sua carta.",
  },
  {
    title: "🏆 Ranking Atualizado!",
    content:
      "Vê quem está no topo da trivia moçambicana e desafia os teus amigos a ultrapassar a tua pontuação!",
  },
  {
    title: "📢 Novidades no Jogo",
    content:
      "Novos desafios sobre cultura e geografia foram adicionados. Teste os teus conhecimentos agora!",
  },
  {
    title: "🧠 Curiosidades",
    content:
      "Aprende factos fascinantes sobre Moçambique enquanto te divertes com os desafios do jogo.",
  },
  {
    title: "🌱 Novos Desafios de Ecologia",
    content:
      "Ajuda a salvar o planeta! Responde a perguntas sobre ambiente, reciclagem e sustentabilidade.",
  },
  {
    title: "🌍 Consciência Ambiental",
    content:
      "O modo EcoKaringana promove o conhecimento sobre práticas sustentáveis e respeito pela natureza.",
  },
  {
    title: "♻️ Quiz Verde",
    content:
      "Domina o quiz de ecologia e mostra que és um verdadeiro guardião do meio ambiente!",
  },
];

  return (
    <motion.div
      style={sliderStyle}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.3 }}
      aria-roledescription="carousel"
      aria-label="Notícias do Karingana"
    >
      <Slider {...sliderSettings}>
        {newsItems.map((item, index) => (
          <div key={index}>
            <NewsCard item={item} isActive={index === activeSlide} />
          </div>
        ))}
      </Slider>
      <style jsx global>{`
        .custom-dots {
          bottom: -30px !important;
        }
        .custom-dots li button:before {
          color: white !important;
          opacity: 0.5;
          font-size: 10px;
        }
        .custom-dots li.slick-active button:before {
          color: white !important;
          opacity: 1;
        }
      `}</style>
    </motion.div>
  );
};

// Componente para Botão de Ação
const ActionButton = ({
  text,
  route,
  icon,
  backgroundColor = "#D32F2F",
  onClick,
}) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const buttonStyle = {
    backgroundColor,
    color: "white",
    padding: "15px 20px",
    fontSize: "clamp(1rem, 1.2vw, 1.3rem)",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: "bold",
    textTransform: "uppercase",
    boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.3)",
    transition: "all 0.3s ease",
    margin: "10px",
    width: "100%",
    maxWidth: "250px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (route) {
      navigate(route);
    }
  };

  return (
    <motion.button
      style={buttonStyle}
      whileHover={{ scale: 1.05, boxShadow: "0px 12px 25px rgba(0, 0, 0, 0.4)" }}
      whileTap={{ scale: 0.95 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={handleClick}
      aria-label={text}
      type="button"
    >
      {/* Efeito de brilho/reflexo */}
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 50%)",
          zIndex: 0,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      />

      {/* Círculos de animação quando hover */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ scale: 0, opacity: 0.7 }}
            animate={{ scale: 1.5, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              border: "2px solid rgba(255,255,255,0.5)",
              zIndex: 0,
              pointerEvents: "none",
            }}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Ícone (opcional) */}
      {icon && <span style={{ fontSize: "1.2em" }}>{icon}</span>}

      {/* Texto do botão */}
      <span style={{ position: "relative", zIndex: 1 }}>{text}</span>
    </motion.button>
  );
};

const Home = () => {
  const [loaded, setLoaded] = useState(false);
  const [temaAtual, setTemaAtual] = useState("cultura"); // novo estado do tema
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaded(true);
      setShowSplash(false);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  if (showSplash) {
    return <SplashScreen />;
  }

  const styles = {
    pageStyle: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url(${
        process.env.PUBLIC_URL +
        (temaAtual === "ecologia" ? "/assets/ecopic.jpg" : "/assets/7.jpg")
      })`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      textAlign: "center",
      position: "relative",
      flexDirection: "column",
      padding: "5vw",
      overflow: "hidden",
    },
    overlayStyle: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background:
        "radial-gradient(circle at center, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.8) 100%)",
      zIndex: 0,
    },
    containerStyle: {
      position: "relative",
      zIndex: 1,
      background: "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)",
      padding: "40px",
      borderRadius: "20px",
      boxShadow: "0px 15px 35px rgba(0, 0, 0, 0.4)",
      maxWidth: "600px",
      width: "100%",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255,255,255,0.1)",
      overflow: "hidden"
    },
    headingStyle: {
      color: "#fff",
      fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
      fontWeight: "bold",
      marginBottom: "10px",
      textShadow: "0px 3px 6px rgba(0,0,0,0.3)",
      fontFamily: "'Montserrat', sans-serif",
      letterSpacing: "1px"
    },
    textStyle: {
      fontSize: "clamp(1.1rem, 3vw, 1.4rem)",
      color: "#fff",
      marginBottom: "35px",
      textShadow: "0px 1px 3px rgba(0,0,0,0.3)",
      lineHeight: 1.5,
      fontWeight: 300
    },
    buttonsContainerStyle: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      zIndex: 1
    },
    logoStyle: {
      fontSize: "3rem",
      marginBottom: "10px",
      color: temaAtual === "ecologia" ? "#66BB6A" : "#FFEB3B",
      textShadow: "0px 0px 15px rgba(255,255,255,0.5)"
    },
    glow: {
      position: "absolute",
      top: "-50%",
      left: "-50%",
      width: "200%",
      height: "200%",
      background: "radial-gradient(circle at center, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)",
      zIndex: 0
    }
  };

  return (
    <motion.div 
      style={styles.pageStyle}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div style={styles.overlayStyle} aria-hidden="true"></div>
      <BackgroundParticles />

      {/* Navbar para troca de tema */}
      <nav style={{
        display: "flex",
        justifyContent: "center",
        gap: "1.5rem",
        zIndex: 2,
        marginBottom: "20px"
      }}>
        <button
          onClick={() => setTemaAtual("cultura")}
          style={{
            backgroundColor: temaAtual === "cultura" ? "#FFD600" : "transparent",
            border: "none",
            color: "#fff",
            fontSize: "1.1rem",
            fontWeight: "bold",
            cursor: "pointer",
            padding: "10px 20px",
            borderRadius: "25px",
            transition: "all 0.3s ease",
            boxShadow: temaAtual === "cultura" ? "0px 4px 12px rgba(0,0,0,0.3)" : "none"
          }}
        >
          🎭 Cultura
        </button>
        <button
          onClick={() => setTemaAtual("ecologia")}
          style={{
            backgroundColor: temaAtual === "ecologia" ? "#66BB6A" : "transparent",
            border: "none",
            color: "#fff",
            fontSize: "1.1rem",
            fontWeight: "bold",
            cursor: "pointer",
            padding: "10px 20px",
            borderRadius: "25px",
            transition: "all 0.3s ease",
            boxShadow: temaAtual === "ecologia" ? "0px 4px 12px rgba(0,0,0,0.3)" : "none"
          }}
        >
          ♻️ Ecologia
        </button>
      </nav>

      <motion.div
        style={styles.containerStyle}
        initial={{ opacity: 0, y: -20 }}
        animate={{ 
          opacity: loaded ? 1 : 0, 
          y: loaded ? 0 : -20,
          transition: { 
            type: "spring", 
            stiffness: 120, 
            damping: 20 
          }
        }}
      >
        <div style={styles.glow} />
        
        <motion.div 
          style={styles.logoStyle}
          animate={{ 
            textShadow: [
              "0px 0px 10px rgba(255,255,255,0.3)", 
              "0px 0px 20px rgba(255,255,255,0.6)", 
              "0px 0px 10px rgba(255,255,255,0.3)"
            ] 
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          {temaAtual === "ecologia" ? "♻️" : "🌍"}
        </motion.div>
        
        <motion.h1 style={styles.headingStyle}>
          {temaAtual === "ecologia" ? "EcoKaringana" : "Karingana"}
        </motion.h1>
        
        <motion.p style={styles.textStyle}>
          {temaAtual === "ecologia"
            ? "Aprende sobre ecologia, meio ambiente e práticas sustentáveis de forma divertida e interativa!"
            : "Testa os teus conhecimentos sobre Moçambique e aprende enquanto te divertes com desafios interativos!"}
        </motion.p>
        
        <div style={styles.buttonsContainerStyle}>
            <ActionButton 
    text="Começar Agora" 
    route={`/quiz?tema=${temaAtual}`}
    icon="🎮" 
    backgroundColor={temaAtual === "ecologia" ? "#66BB6A" : "rgba(211, 47, 47, 0.9)"}
  />
  <ActionButton
  text="📚 Biblioteca"
  route="/biblioteca"
  backgroundColor="#1565C0"
/>
  {temaAtual === "ecologia" ? (
  <ActionButton 
    text="Ver Ranking (Brevemente)" 
    icon="⏳" 
    backgroundColor="#BDBDBD" 
    onClick={() => alert("O ranking de Ecologia estará disponível brevemente!")} 
  />
) : (
  <ActionButton 
    text="Ver Ranking" 
    route="/ranking" 
    icon="🏆" 
    backgroundColor="rgba(46, 125, 50, 0.9)" 
  />
  
)}

</div>


        <HighlightBadge text="NOVO!" />
      </motion.div>

      <NewsSlider />
      <KaringanaInstallPrompt />
      <KaringanaBotWidget />
      <WhatsAppSupportButton />
      
      {/* Footer minimalista */}
      <footer
        style={{
          width: "100%",
          textAlign: "center",
          marginTop: 32,
          marginBottom: 8,
          fontSize: "0.95rem",
          color: "#e0e0e0",
          letterSpacing: "0.02em",
          fontWeight: 400,
          opacity: 0.85,
          zIndex: 2,
          position: "relative"
        }}
      >
        Desenvolvido pela &nbsp;
        <a
          href="https://ivaneusebio.vercel.app"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#D32F2F", textDecoration: "none", fontWeight: 600 }}
        >
        UnityMoz
        </a>
      </footer>
    </motion.div>
  );
};

export default Home;
